import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModuleLink as Link } from '../../components/ModuleLink';
import { useApiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { fmtDate } from '../../utils';
import type { StakeholderResponse, ChangeRequest } from '../../types';
import Layout from '../../components/Layout';

export default function StakeholderResponse() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') ?? '';
  const cid = searchParams.get('cid') ?? '';
  const api = useApiClient();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<
    'form' | 'done' | 'inactive' | 'confirmed'
  >('form');
  const [change, setChange] = useState<ChangeRequest | null>(null);
  const [isAffected, setIsAffected] = useState('');
  const [howAffected, setHowAffected] = useState('');
  const [teamRole, setTeamRole] = useState('');
  const [supportNeeds, setSupportNeeds] = useState('');
  const [concerns, setConcerns] = useState('');
  const [effortEstimate, setEffortEstimate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!id || !cid) {
      setLoading(false);
      return;
    }
    async function load() {
      try {
        const [respData, changeData] = await Promise.all([
          api.publicGet<StakeholderResponse>(`/responses/${encodeURIComponent(id)}`),
          api.publicGet<ChangeRequest>(`/changes/${encodeURIComponent(cid)}`),
        ]);
        setChange(changeData as ChangeRequest);
        if ((respData as StakeholderResponse).responseStatus === 'Responded') {
          setState('done');
          setIsAffected(respData.isAffected ?? '');
          setHowAffected(respData.howAffected ?? '');
          setTeamRole(respData.teamRole ?? '');
          setSupportNeeds(respData.supportNeeds ?? '');
          setConcerns(respData.concerns ?? '');
          setEffortEstimate(respData.effortEstimate ?? '');
        } else if (
          ['Closed', 'Rejected'].includes((changeData as ChangeRequest).status)
        ) {
          setState('inactive');
        }
      } catch {
        showToast('Failed to load record.', 'error');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, cid]);

  function validate() {
    const errs: Record<string, string> = {};
    if (!isAffected) errs.isAffected = 'Please select yes or no.';
    if (isAffected === 'Yes') {
      if (!howAffected.trim())
        errs.howAffected = 'Please describe how you are affected.';
      if (!teamRole.trim()) errs.teamRole = 'Please enter your team role.';
      if (!supportNeeds.trim())
        errs.supportNeeds = 'Please describe your support needs.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        isAffected,
        howAffected: isAffected === 'Yes' ? howAffected.trim() : undefined,
        teamRole: isAffected === 'Yes' ? teamRole.trim() : undefined,
        supportNeeds: isAffected === 'Yes' ? supportNeeds.trim() : undefined,
        concerns: concerns.trim() || undefined,
        effortEstimate:
          isAffected === 'Yes' ? effortEstimate || undefined : undefined,
        responseStatus: 'Responded',
        dateResponded: new Date().toISOString(),
      };
      await api.publicPatch(`/responses/${encodeURIComponent(id)}`, payload);
      setState('confirmed');
    } catch {
      showToast('Submission failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout maxWidth='form'>
          <nav className='breadcrumb'>
            <Link to='/' className='breadcrumb__link'><i className='fa-solid fa-house' aria-hidden='true' /> Home</Link>
            <span className='breadcrumb__sep'>/</span>
            <span className='breadcrumb__current'>Stakeholder Response</span>
          </nav>
          {loading && (
            <div className='loading-state'>
              <div className='spinner spinner--lg' />
              <span>Loading…</span>
            </div>
          )}

        {!loading && state === 'done' && (
          <div>
            <div className='state-card'>
              <div style={{ fontSize: '3rem', color: 'var(--accent)' }}>
                <i className='fa-solid fa-circle-check' aria-hidden='true' />
              </div>
              <h1 className='state-card__title'>Already Responded</h1>
              <p className='state-card__body'>
                Your response has already been recorded. No further action is
                required.
              </p>
            </div>
            <Link to='/' className='btn btn--secondary' style={{ marginTop: 16 }}>
              <i className='fa-solid fa-house' aria-hidden='true' /> Back to Home
            </Link>
          </div>
        )}

        {!loading && state === 'inactive' && (
          <div>
            <div className='state-card'>
              <div className='state-card__icon'>
                <i className='fa-solid fa-ban' aria-hidden='true' />
              </div>
              <h1 className='state-card__title'>Change No Longer Active</h1>
              <p className='state-card__body'>
                This change is no longer active. Response is not required.
              </p>
            </div>
            <Link to='/' className='btn btn--secondary' style={{ marginTop: 16 }}>
              <i className='fa-solid fa-house' aria-hidden='true' /> Back to Home
            </Link>
          </div>
        )}

        {!loading && state === 'confirmed' && (
          <div>
            <div className='alert alert--success'>
              <span className='alert__icon'>
                <i className='fa-solid fa-circle-check' aria-hidden='true' />
              </span>
              <div className='alert__body'>
                <div className='alert__title'>Response recorded.</div>
                <p>
                  Thank you. The Change Lead has been notified of your response.
                </p>
              </div>
            </div>
            <Link to='/' className='btn btn--secondary' style={{ marginTop: 16 }}>
              <i className='fa-solid fa-house' aria-hidden='true' /> Back to Home
            </Link>
          </div>
        )}

        {!loading && state === 'form' && change && (
          <div>
            <div style={{ marginBottom: 4 }}>
              <span className='badge badge--primary'>{change.changeId}</span>
              <h1 style={{ marginTop: 6, fontSize: '1.375rem' }}>
                {change.changeName}
              </h1>
              <p className='text-muted'>
                {change.scope} · Proposed Go-Live:{' '}
                {fmtDate(change.proposedGolive)}
              </p>
            </div>
            <div className='divider' />

            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 700,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <i
                className='fa-solid fa-info-circle'
                style={{ color: 'var(--primary)' }}
                aria-hidden='true'
              />{' '}
              Change Summary
            </h2>
            <div className='field-display-grid' style={{ marginBottom: 24 }}>
              {[
                ['Current State', change.currentState],
                ['New State', change.newState],
                ['Scope', change.scope],
                ['Benefits/Reason', change.benefitsReason],
              ].map(([l, v]) => (
                <div key={l as string} className='field-display'>
                  <div className='field-display__label'>{l as string}</div>
                  <div className='field-display__value'>{v ?? '—'}</div>
                </div>
              ))}
            </div>

            <div className='divider' />
            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 700,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <i
                className='fa-solid fa-comment-dots'
                style={{ color: 'var(--primary)' }}
                aria-hidden='true'
              />{' '}
              Your Response
            </h2>

            <div className='form-group' style={{ marginBottom: 20 }}>
              <label className='form-label'>
                How will this change affect you?{' '}
                <span className='required'>*</span>
              </label>
              <div className='pill-group'>
                {['Yes', 'No'].map((v) => (
                  <button
                    key={v}
                    type='button'
                    className={`pill-option${isAffected === v ? ' is-selected' : ''}`}
                    onClick={() => {
                      setIsAffected(v);
                      setErrors({});
                    }}
                  >
                    {v === 'Yes' ? (
                      <i
                        className='fa-solid fa-circle-check'
                        aria-hidden='true'
                      />
                    ) : (
                      <i
                        className='fa-solid fa-circle-xmark'
                        aria-hidden='true'
                      />
                    )}{' '}
                    {v === 'Yes'
                      ? 'This will affect me'
                      : 'This will not affect me'}
                  </button>
                ))}
              </div>
              {errors.isAffected && (
                <span className='form-error'>
                  <i
                    className='fa-solid fa-circle-exclamation'
                    aria-hidden='true'
                  />{' '}
                  {errors.isAffected}
                </span>
              )}
            </div>

            {isAffected === 'Yes' && (
              <>
                <div className='form-group' style={{ marginBottom: 20 }}>
                  <label className='form-label' htmlFor='how-affected'>
                    How will you be affected?{' '}
                    <span className='required'>*</span>
                  </label>
                  <textarea
                    id='how-affected'
                    className={`form-control${errors.howAffected ? ' is-error' : ''}`}
                    rows={3}
                    value={howAffected}
                    onChange={(e) => setHowAffected(e.target.value)}
                    placeholder='Describe how this change affects your work…'
                  />
                  {errors.howAffected && (
                    <span className='form-error'>
                      <i
                        className='fa-solid fa-circle-exclamation'
                        aria-hidden='true'
                      />{' '}
                      {errors.howAffected}
                    </span>
                  )}
                </div>

                <div className='form-group' style={{ marginBottom: 20 }}>
                  <label className='form-label' htmlFor='team-role'>
                    Your team role <span className='required'>*</span>
                  </label>
                  <input
                    id='team-role'
                    type='text'
                    className={`form-control${errors.teamRole ? ' is-error' : ''}`}
                    value={teamRole}
                    onChange={(e) => setTeamRole(e.target.value)}
                    placeholder='e.g., Business Analyst, End User, Support Team…'
                  />
                  {errors.teamRole && (
                    <span className='form-error'>
                      <i
                        className='fa-solid fa-circle-exclamation'
                        aria-hidden='true'
                      />{' '}
                      {errors.teamRole}
                    </span>
                  )}
                </div>

                <div className='form-group' style={{ marginBottom: 20 }}>
                  <label className='form-label' htmlFor='support-needs'>
                    What support do you need?{' '}
                    <span className='required'>*</span>
                  </label>
                  <textarea
                    id='support-needs'
                    className={`form-control${errors.supportNeeds ? ' is-error' : ''}`}
                    rows={3}
                    value={supportNeeds}
                    onChange={(e) => setSupportNeeds(e.target.value)}
                    placeholder='e.g., Training, Documentation, Parallel running period…'
                  />
                  {errors.supportNeeds && (
                    <span className='form-error'>
                      <i
                        className='fa-solid fa-circle-exclamation'
                        aria-hidden='true'
                      />{' '}
                      {errors.supportNeeds}
                    </span>
                  )}
                </div>

                <div className='form-group' style={{ marginBottom: 20 }}>
                  <label className='form-label' htmlFor='effort-estimate'>
                    Effort estimate to adapt to this change
                  </label>
                  <input
                    id='effort-estimate'
                    type='text'
                    className='form-control'
                    value={effortEstimate}
                    onChange={(e) => setEffortEstimate(e.target.value)}
                    placeholder='e.g., 1 day, 1 week, 2-3 weeks…'
                  />
                </div>
              </>
            )}

            <div className='form-group' style={{ marginBottom: 24 }}>
              <label className='form-label' htmlFor='concerns'>
                Do you have any concerns or questions?
                <span
                  className='badge badge--muted'
                  style={{ fontWeight: 400, marginLeft: 6 }}
                >
                  Optional
                </span>
              </label>
              <textarea
                id='concerns'
                className='form-control'
                rows={3}
                value={concerns}
                onChange={(e) => setConcerns(e.target.value)}
                placeholder='Describe any concerns or questions you have about this change…'
              />
            </div>

            <button
              className={`btn btn--primary btn--lg w-full${submitting ? ' is-loading' : ''}`}
              onClick={submit}
              disabled={submitting}
            >
              <i className='fa-solid fa-paper-plane' aria-hidden='true' />{' '}
              Submit Response
            </button>
          </div>
        )}
    </Layout>
  );
}
